import {IReg, IRegPointer, IUiEnv, REG} from "/@lib@/commons/registry.js";
import {BasicUniverse, ICoreUniverseEnv, IUniverseEnv, OBasicUniverseConfig} from "/@lib@/core/universe.js";
import {IDepotEnv, IDepotUiEnv, isDepotUiEnv} from "/@lib@/store/depot.js";
import {IEndPoint} from "/@lib@/commons/io/io.js";
import {JSX} from "/@lib@/commons/xml/dom.js";
import {IUtBrowserCtx, OUtBrowserInit, UtBrowser, UtBrowserFetcher} from "/@back@/store/views/utBrowser.js";
import {AccelKeyMgr} from "/@lib@/commons/actions.js";
import {Area} from "/@lib@/commons/areas.js";
import {IChainEnv} from "/@lib@/wsp/chain.js";
import {IInfoBrokerPointer} from "/@lib@/commons/infos.js";
import {IView} from "/@lib@/commons/views.js";
import {JLastDatas} from "/@lib@/commons/lastDatas.js";
import {MsgLabel, OMsgLabelInit} from "/@back@/commons/basis.js";

/**
 * Paramètres d'initialisation de l'embedded App passé par l'appelant.
 */
export interface OEmbeddedDepotInit {
	/** Si auth mutualisée avec le contexte appelant. */
	authReg?: IReg<ICoreUniverseEnv>

	/** Si le dépot est dispo dans le contexte appelant (authReg alors obligatoire). */
	depotReg?: IReg<IDepotEnv>

	/** Node parent dans lequel insérer l'interface graphique. */
	parentNode: HTMLElement
}

/** Api du module dans le front. */
export type embeddedDepotModule = { initEmbeddedApp: (init: OEmbeddedDepotInit) => Promise<void> }

/**
 * Paramètres de configuration de la factory de la fonction d'init de l'embedded App
 * (Paramètres passés par le module du front).
 */
export interface OEmbeddedDepotFactoryConfig {
	/** Langue utilisée. */
	lang: string

	/** Configuration de l'univers racine porteur de l'auth et de la connexion websocket si gestion autonome de l'auth. */
	rootUniverseConf: OBasicUniverseConfig

	/** Path du fichier de config du linker pour la gestion autonome de l'auth. */
	authConfPath: IEndPoint

	/** Path du module de configuration du dépot si le depotReg n'est pas  fourni par le contexte appelant. */
	depotConfPath: IEndPoint

	/** Si le dépot et l'auth utilisent le même univers java. */
	depotWs?: 'auth' | null
}

/**
 * Construit la fonction d'initialisation de l'embedded App que le module front
 * doit exporter avec la clé "initEmbeddedApp".
 */
export function embeddedAppFactory(config: OEmbeddedDepotFactoryConfig) {
	return async function initEmbeddedApp(init: OEmbeddedDepotInit): Promise<void> {
		let depotReg: IReg<IDepotEnv>;
		if (!init.authReg) {
			//Mode 100% autonome.
			REG.reg.setPref("lang", 21, config.lang);
			new BasicUniverse(config.rootUniverseConf);
			const [authConf, depotConf] = await Promise.all([
				config.authConfPath.importJs() as Promise<{ init: (reg: IReg<ICoreUniverseEnv>, wsReg: IReg<IUniverseEnv>) => void }>,
				config.depotConfPath.importJs() as Promise<{ init: (reg: IReg<IDepotEnv>, authReg: IReg<ICoreUniverseEnv>, wsReg: IReg<IUniverseEnv>) => void }>]);
			const authReg = REG.createSubReg<ICoreUniverseEnv>(REG.reg);
			authConf.init(authReg, authReg.parentReg);
			depotReg = REG.createSubReg<IDepotEnv>(REG.reg);
			depotConf.init(depotReg, authReg, config.depotWs === "auth" ? authReg : null);
		} else {
			//auth mutualisée
			if (init.depotReg) {
				//depot dispo dans le contexte appelant.
				depotReg = init.depotReg;
			} else {
				//Chargement autonome du dépot
				depotReg = REG.createSubReg<IDepotEnv>(REG.reg);
				const depotConf = await config.depotConfPath.importJs() as { init: (reg: IReg<IDepotEnv>, authReg: IReg<ICoreUniverseEnv>, wsReg: IReg<IUniverseEnv>) => void };
				depotConf.init(depotReg, init.authReg, null);
			}
		}
		let depotUiReg: IReg<IDepotUiEnv> = isDepotUiEnv(depotReg.env) ? depotReg : depotReg.env.universe.newDepotUiReg(init.parentNode);
		//depotReg dispo
		//TODO auth autonome : injecter bouton c-user-login
		const utBrowser = new UtBrowser().initialize({
			reg: depotUiReg,
			autoCloseReg: depotReg !== depotUiReg,
			fetcher: new UtBrowserFetcher(depotReg.env.universe.adminUrlTree), //.setFilter("usable"),
			actions: depotReg.mergeLists("actions:embeddedDepot:utBrowser", "actions:store:resList"),
			accelKeyMgr: new AccelKeyMgr<IUtBrowserCtx>().initFromMapActions(depotReg.mergeListsAsMap("accelkeys:embeddedDepot:utBrowser", "accelkeys:store:resList")),
		} as OUtBrowserInit);
		init.parentNode.textContent = null;
		init.parentNode.appendChild(utBrowser);
		//init.parentNode.appendChild(<div>{JSON.stringify(depotReg.getRegDef())}</div>);
	}
}

/**
 * Area construisant une vue d'un dépot
 */
export class ForeignDepotArea extends Area<IRegPointer<IChainEnv & IInfoBrokerPointer & IUiEnv>, IView> {
	constructor(id: string, public frontJsModule: IEndPoint, public authReg?: IReg<ICoreUniverseEnv>, public depotReg?: IReg<any>) {
		super(id);
		this._label = "Dépôt";
		this._icon = "/@skin@/wsp/views/depot/depot.svg";
	}

	buildBody(ctx: IRegPointer<IChainEnv & IInfoBrokerPointer & IUiEnv>, lastDatas?: JLastDatas): IView {
		const root = <div style="flex:1; display:flex; min-width:0; min-height:0">
			<c-msg î={{standardMsg: "loading"} as OMsgLabelInit}/>
		</div> as HTMLElement;
		const embeddedDepot = this.frontJsModule.importJs() as Promise<embeddedDepotModule>;
		embeddedDepot.then(m => {
			return m.initEmbeddedApp({
				authReg: this.authReg,
				depotReg: this.depotReg,
				parentNode: root
			});
		}).catch((e) => {
			const msg = root.firstElementChild as MsgLabel;
			msg.setStandardMsg("netError");
			throw e;
		});
		return root;
	}
}
